<template>
    <RightSheet style="width: 90vw;" v-model="dialogVisible" max-width="1920">
        <div class="dialog-wrapper">
            <!-- 标题 -->
            <div class="dialog-title">
                <v-btn icon variant="text" class="back-btn" @click="closeDialog">
                    <v-icon>mdi-reply-outline</v-icon>
                    返回
                </v-btn>
                {{ elink?.eLinkName }} - 详情
                <span class="sample-time">采样时间：{{ currentTime }}</span>
            </div>

            <!-- 主内容区域 -->
            <div class="dialog-main detail-dialog-main">
                <!-- 左侧：放大图片 -->
                <div class="detail-left-content">
                    <div class="enlarged-image-container">
                        <img src="@/assets/images/station-monitor/detail-dialog/elink-device.png"
                            class="enlarged-device-img" />
                        <div class="enlarged-device-name">{{ elink.productModel }}</div>
                    </div>
                </div>

                <!-- 右侧：五列详细值 -->
                <div class="detail-right-content">
                    <div class="detail-column" v-for="(column, colIndex) in detailColumns" :key="colIndex">
                        <div class="column-header">{{ column.title }}</div>
                        <div class="detail-card" v-for="(item, itemIndex) in column.items" :key="itemIndex">
                            <div class="card-value" :class="{ 'status-text': item.isStatus }">
                                <span v-if="item.isStatus" class="status-dot"
                                    :style="{ backgroundColor: item.statusColor }"></span>
                                {{ item.value }}
                            </div>
                            <div class="card-sub-label" v-if="item.subLabel">{{ item.subLabel }}</div>
                            <div class="card-label" v-else>{{ item.label }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 关闭按钮 -->
            <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </div>
    </RightSheet>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { ws_req } from '@/ws/wss-request'
import { useStationContext } from '../../composables/useStationContext'
import {
    ELinkWorkStatus,
    ELinkStatusColor,
    AcQfStatus,
    PowerSwitchStatus,
    ThirdCtlStatus,
    formatStatus
} from './monitorEnums'
import RightSheet from '@/components/RightSheet/index.vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    elink: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
})

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId } = useStationContext()

const currentTime = ref(new Date().toLocaleString())
const pollingTimer = ref(null)

const closeDialog = () => {
    dialogVisible.value = false
}



// 详情页数据列配置
const detailColumns = ref([
    {
        title: '运行状态',
        items: [
            { label: '系统运行状态', value: '--', isStatus: true, statusColor: '#999999' },
            { label: '断路器状态', value: '--', subLabel: '断路器状态' },
            { label: '开关状态', value: '--', subLabel: '开关状态' }
        ]
    },
    {
        title: '电网电流',
        items: [
            { label: '电网A相总电流', value: '0A' },
            { label: '电网B相总电流', value: '0A' },
            { label: '电网C相总电流', value: '0A' }
        ]
    },
    {
        title: '运行功率',
        items: [
            { label: '总有功功率', value: '0kW' },
            { label: '总无功功率', value: '0kW' },
            { label: '电池组最大可充电功率', value: '0kW' },
            { label: '电池组最大可放电功率', value: '0kW' }
        ]
    },
    {
        title: '虚拟电厂控制参数',
        items: [
            { label: '最大可充功率', value: '0kW' },
            { label: '最大可放功率', value: '0kW' },
            { label: '第三方控制使能', value: '--' },
            { label: '第三方控制功率值', value: '0kW' }
        ]
    },
    {
        title: '统计信息',
        items: [
            { label: '系统剩余可放电量', value: '0kWh' },
            { label: '日总充电量', value: '0kWh' },
            { label: '日总放电量', value: '0kWh' },
            { label: '总SOC', value: '0%' },
            { label: '系统CPU占用率', value: '0%' },
            { label: '系统内存占用', value: '0%' },
            { label: '文件系统占用率', value: '0%' }
        ]
    }
])



// 更新详情数据
const updateDetailData = (data) => {
    if (!data) return

    // 更新采样时间
    if (data.record_time) {
        currentTime.value = data.record_time
    }

    // 运行状态列
    detailColumns.value[0].items[0].value = formatStatus(data.run_st, ELinkWorkStatus)
    detailColumns.value[0].items[0].statusColor = ELinkStatusColor[data.run_st] || ELinkStatusColor['0']
    detailColumns.value[0].items[1].value = formatStatus(data.ac_qf_st, AcQfStatus)
    detailColumns.value[0].items[2].value = formatStatus(data.power_on, PowerSwitchStatus)

    // 电网电流列
    detailColumns.value[1].items[0].value = `${data.i_a || '0'}A`
    detailColumns.value[1].items[1].value = `${data.i_b || '0'}A`
    detailColumns.value[1].items[2].value = `${data.i_c || '0'}A`

    // 运行功率列
    detailColumns.value[2].items[0].value = `${(data.p_total || 0).toFixed(2)}kW`
    detailColumns.value[2].items[1].value = `${(data.q_total || 0).toFixed(2)}kVar`
    detailColumns.value[2].items[2].value = `${(data.p_ch_max || 0).toFixed(2)}kW`
    detailColumns.value[2].items[3].value = `${(data.p_disch_max || 0).toFixed(2)}kW`

    // 虚拟电厂控制参数列
    detailColumns.value[3].items[0].value = `${(data.p_ch_demand_max || 0).toFixed(2)}kW`
    detailColumns.value[3].items[1].value = `${(data.p_disch_demand_max || 0).toFixed(2)}kW`
    detailColumns.value[3].items[2].value = formatStatus(data.third_ctl_en, ThirdCtlStatus)
    detailColumns.value[3].items[3].value = `${data.third_ctl_power || '0'}kW`

    // 统计信息列
    detailColumns.value[4].items[0].value = `${data.e_dischargeable || '0'}kWh`
    detailColumns.value[4].items[1].value = `${data.TDC || '0'}kWh`
    detailColumns.value[4].items[2].value = `${data.TDD || '0'}kWh`
    detailColumns.value[4].items[3].value = `${data.soc || '0'}%`
    detailColumns.value[4].items[4].value = `${data.cpu_usage || '0'}%`
    detailColumns.value[4].items[5].value = `${data.mem_usage || '0'}%`
    detailColumns.value[4].items[6].value = `${data.fs_usage || '0'}%`
}

const realData = ref({})
// 获取 elink 详情数据
const fetchElinkDetail = async () => {
    try {
        if (!props.elink?.uuid || !currentStationId.value) {
            return
        }

        const result = await ws_req.request('elink_detail', {
            stationId: Number(currentStationId.value),
            eLinkId: props.elink.id,
            uuid: props.elink.uuid,
        })
        realData.value = result

        if (result && result.data) {
            updateDetailData(result.data)
        }
    } catch (error) {
        console.error('获取 elink 详情失败:', error)
    }
}

// 开始轮询
const startPolling = () => {
    stopPolling()
    fetchElinkDetail()
    pollingTimer.value = setInterval(fetchElinkDetail, 5000) // 每5秒更新一次
}

// 停止轮询
const stopPolling = () => {
    if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
    }
}

// Watch for dialog opening to fetch data
watch(dialogVisible, (newVal) => {
    if (newVal) {
        currentTime.value = new Date().toLocaleString()
        // 更新初始状态
        if (props.elink && detailColumns.value[0] && detailColumns.value[0].items[0]) {
            detailColumns.value[0].items[0].value = props.elink.status
            detailColumns.value[0].items[0].statusColor = props.elink.statusColor
        }
        // 开始获取详情数据
        startPolling()
    } else {
        stopPolling()
    }
})

// 清理定时器
onUnmounted(() => {
    stopPolling()
})
</script>

<style scoped lang="scss">


.dialog-wrapper {
    position: relative;
    width: 100%;
    height: 100vh;
    padding: 20px 24px 0;
    background-color: #fff !important;
    background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
    background-size: cover;
    border-radius: 30px 30px 0 0 !important;
}

.dialog-title {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 20px;
    color: rgba(46, 46, 46, 1);
    font-weight: normal;
    margin-bottom: 20px;
    padding-left: 24px;
    display: flex;
    align-items: center;
}

.close-btn {
    position: absolute;
    top: 16px;
    right: 24px;
    z-index: 10;
}

.dialog-main {
    display: flex;
    gap: 30px;
    height: calc(100% - 83px);
    background: rgba(246, 251, 255, 1);
    border-radius: 30px;
    padding: 20px;
}

/* 详情弹框样式 */
.detail-dialog-main {
    padding: 40px !important;
    gap: 40px !important;
}

.back-btn {
    width: 98px;
    height: 40px;
    border-radius: 12px;
    border: 1px solid #C0CFDA;
    margin-right: 10px;
    margin-left: -10px;
}

.sample-time {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    position: absolute;
    right: 84px;
    top: 26px;
}

.detail-left-content {
    width: 448px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.enlarged-image-container {
    width: 448px;
    height: 600px;
    background: linear-gradient(180deg, rgba(239, 244, 251, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
}

.enlarged-device-img {
    width: 142px;
    /* 放大2倍左右 */
    height: 358px;
}

.enlarged-device-name {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 16px;
    color: rgba(46, 46, 46, 1);
}

.detail-right-content {
    flex: 1;
    display: flex;
    gap: 20px;
    overflow-x: auto;
}

.detail-column {
    flex: 1;
    min-width: 180px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.column-header {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 16px;
    color: rgba(46, 46, 46, 1);
    margin-bottom: 8px;
}

.detail-card {
    background: rgba(239, 244, 251, 0.70);
    border-radius: 10px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 80px;
    justify-content: center;
}

.card-label {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
}

.card-value {
    font-size: 16px;
    color: rgba(46, 46, 46, 1);
    font-weight: 500;
}

.card-sub-label {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
}

.status-text {
    display: flex;
    align-items: center;
    gap: 6px;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
</style>
